<template>
  <div>
    <!-- Health Assessments Table -->
    <b-card class="health-assessments-list-card">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper"></div>
      <!-- Table Header -->
      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
        <div>
          <h5 class="mb-0">Health Assessments</h5>
          <small class="text-muted">Manage and review student health assessments</small>
        </div>
      </div>

      <!-- Filter Row -->
      <b-row class="align-items-center filter-row-single-line mb-2">
        <!-- Search Input -->
        <b-col xl="4" lg="4" md="5" sm="6" cols="12">
              <b-form-input
            id="search"
                v-model="searchQuery"
                placeholder="Search by student name, health status, blood group..."
            class="filter-control"
                @input="onSearchChange"
            debounce="500"
              />
            </b-col>

        <!-- Date Range Filter -->
        <b-col xl="4" lg="4" md="5" sm="12" cols="12">
          <div class="d-flex align-items-center gap-2">
            <flat-pickr
              v-model="filters.dateRange"
              :config="dateRangeConfig"
              class="form-control filter-control"
              placeholder="Date Range"
              @on-change="onDateRangeChange"
            />
            <b-button 
              v-if="hasActiveFilters" 
              variant="outline-secondary" 
              @click="clearFilters" 
              class="clear-btn-circle"
              v-b-tooltip.hover.top="'Clear Filters'"
            >
                <feather-icon icon="XIcon" size="16" />
              </b-button>
          </div>
            </b-col>

        <!-- Action Buttons -->
        <b-col xl="4" lg="4" md="12" sm="12" cols="12" class="text-right">
          <div class="d-flex align-items-center justify-content-end action-buttons-group">
            <b-button v-if="rights.view" variant="outline-primary" @click="openPrintModal" class="print-btn-icon">
                <feather-icon icon="PrinterIcon" size="16" />
              </b-button>
            <b-button v-if="rights.add" variant="gradient-primary" @click="openAddModal" class="action-btn-filter" :disabled="loading">
              <b-spinner v-if="loading" small class="mr-1" />
              <feather-icon v-else icon="PlusIcon" size="16" class="mr-1" />
              Add Assessment
            </b-button>
          </div>
        </b-col>
      </b-row>

        <!-- Pagination Controls - Top (Compact Single Line) -->
        <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-3">
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <!-- Pagination Switch -->
            <b-form-checkbox
              v-model="showPagination"
              switch
              size="sm"
              class="pagination-switch-compact"
            >
              <small class="text-muted">Pagination</small>
            </b-form-checkbox>

            <!-- Page Size Dropdown -->
            <div class="page-size-wrapper">
              <label class="page-size-label">Show:</label>
              <v-select
                :key="'page-size-' + perPage"
                v-model="perPage"
                :options="pageSizeOptions"
                :reduce="option => option.value"
                label="label"
                :clearable="false"
                :searchable="false"
                append-to-body
                class="page-size-select-compact"
                @input="onPageSizeChange"
                @option:selected="closePageSizeDropdown"
                ref="pageSizeSelect"
              />
            </div>
          </div>

          <!-- Count Display in Middle -->
          <div class="pagination-count-display">
            <span class="count-text">
              Showing <strong>{{ displayedRange.start }}</strong> to <strong>{{ displayedRange.end }}</strong> of <strong>{{ totalCount }}</strong>
            </span>
          </div>

          <!-- Pagination -->
          <div v-if="showPagination" class="pagination-wrapper-compact">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalCount"
              :per-page="perPage"
              size="sm"
              align="center"
              class="pagination-compact"
              @change="onPageChange"
            />
          </div>
        </div>

      <!-- Data Grid with Horizontal Scroll -->
      <div class="table-scroll-container">
        <!-- Top Scrollbar for PC -->
        <div class="table-scrollbar-top" ref="topScrollbar">
          <div class="table-scrollbar-content"></div>
        </div>
        
        <!-- Table Wrapper -->
        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
      <b-table
            class="health-assessments-table"
        :items="filteredAssessments"
        :fields="fields"
        :busy="dataLoading"
            :responsive="false"
        hover
        show-empty
            :empty-text="tableEmptyText"
      >
        <template #table-busy>
              <tbody>
                <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                  <td v-for="(field, index) in fields" :key="'skeleton-cell-' + index" class="skeleton-cell">
                    <div class="skeleton-loader"></div>
                  </td>
                </tr>
              </tbody>
        </template>
        
        <template #cell(sno)="{ index }">
          <span class="font-weight-bold">{{ (currentPage - 1) * perPage + index + 1 }}</span>
        </template>

        <template #cell(assessmentDate)="{ item }">
          <span class="font-weight-bold">{{ item.assessmentDate || 'N/A' }}</span>
        </template>

        <template #cell(assessmentTime)="{ item }">
          <span class="font-weight-bold">{{ formatTime(item.assessmentTime) }}</span>
        </template>

        <template #cell(studentName)="{ item }">
          <span class="font-weight-bold text-primary">{{ item.studentName || 'N/A' }}</span>
        </template>


        <template #cell(BMI)="{ item }">
          <b-badge 
            :variant="getBMIVariant(item.BMI)"
          >
            {{ item.BMI }}
          </b-badge>
        </template>

        <template #cell(healthStatus)="{ item }">
          <b-badge 
            :variant="getHealthStatusVariant(item.healthStatus)"
          >
            {{ item.healthStatus }}
          </b-badge>
        </template>

        <template #cell(actions)="{ item }">
          <div class="d-flex align-items-center justify-content-end">
                <b-button
                  variant="gradient-primary"
                  size="sm"
                  v-b-tooltip.hover.left
                  title="View Details"
                  class="action-btn-table mr-1"
                  @click="viewDetails(item)"
                  :disabled="isActionLoading(item, 'view') || isActionLoading(item, 'edit') || isActionLoading(item, 'delete')"
                >
                  <b-spinner v-if="isActionLoading(item, 'view')" small type="grow" />
                  <feather-icon v-else icon="EyeIcon" size="16" />
                </b-button>

                <b-button
                  v-if="rights.edit"
                  variant="gradient-primary"
                  size="sm"
                  v-b-tooltip.hover.left
                  title="Edit"
                  class="action-btn-table mr-1"
                  @click="editAssessment(item)"
                  :disabled="isActionLoading(item, 'edit') || isActionLoading(item, 'delete')"
                >
                  <b-spinner v-if="isActionLoading(item, 'edit')" small type="grow" />
                  <feather-icon v-else icon="EditIcon" size="16" />
                </b-button>

                <b-button
                  v-if="rights.delete"
                  variant="outline-danger"
                  size="sm"
                  v-b-tooltip.hover.right
                  title="Delete"
                  class="action-btn-outline"
                  @click="deleteAssessment(item)"
                  :disabled="isActionLoading(item, 'delete') || isActionLoading(item, 'edit')"
                >
                  <b-spinner v-if="isActionLoading(item, 'delete')" small type="grow" />
                  <feather-icon v-else icon="Trash2Icon" size="16" />
                </b-button>
          </div>
        </template>
      </b-table>
        </div>

        <!-- Bottom Scrollbar for PC -->
        <div class="table-scrollbar-bottom" ref="bottomScrollbar" @scroll="syncScroll">
          <div class="table-scrollbar-content"></div>
        </div>
      </div>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Health Assessment' : 'Add Health Assessment'"
      @hidden="resetForm"
      centered
      no-close-on-backdrop
      size="lg"
      modal-class="assessment-modal"
    >
      <b-form @submit.prevent class="mt-2">
        <!-- Date and Time fields at the top -->
        <div class="form-card soft-card">
        <b-row>
          <b-col md="6">
            <b-form-group label="Assessment Date" label-for="assessmentDate">
              <flat-pickr
                v-model="form.assessmentDate"
                :config="dateConfig"
                class="form-control input-compact"
                :placeholder="$t('Select Date')"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Assessment Time" label-for="assessmentTime">
              <flat-pickr
                v-model="form.assessmentTime"
                :config="timeConfig"
                class="form-control input-compact"
                placeholder="Select time"
              />
            </b-form-group>
          </b-col>
        </b-row>
        </div>

        <hr class="my-2">

        <div class="form-card soft-card">
        <b-row>
          <b-col md="8">
            <b-form-group label="Student" label-for="student" required>
              <!-- Search Input -->
              <div class="search-section">
                <b-form-input
                  v-model="studentSearchQuery"
                  placeholder="Search students..."
                  @input="onStudentSearchChange"
                  class="search-input input-compact"
                />
              </div>

              <!-- Search Results -->
              <div v-if="studentSearchQuery.length >= 2" class="search-results">
                <div v-if="filteredStudentOptions.length > 0" class="student-options">
                  <div
                    v-for="student in filteredStudentOptions"
                    :key="student.id"
                    class="student-option"
                    @click="selectStudent(student)"
                  >
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{{ student.name }}</strong>
                        <div class="text-muted small">
                          {{ student.className }}{{ student.sectionName ? ' - ' + student.sectionName : '' }}
                          <span v-if="student.grNo" class="ml-2">
                            GR: {{ student.grNo }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="filteredStudentOptions.length === 0 && !studentSearchLoading" class="empty-state">
                  <feather-icon icon="SearchIcon" size="32" />
                  <p>No students found</p>
                </div>

                <div v-if="studentSearchLoading" class="loading-state">
                  <b-spinner variant="primary" size="sm" />
                  <span>Searching...</span>
                </div>
              </div>

              <!-- Selected Student Display -->
              <div v-if="form.sID && form.sID.name" class="selected-student">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{{ form.sID.name }}</strong>
                    <div class="text-muted small">
                      {{ form.sID.className }}{{ form.sID.sectionName ? ' - ' + form.sID.sectionName : '' }}
                      <span v-if="form.sID.grNo" class="ml-2">
                        GR: {{ form.sID.grNo }}
                      </span>
                    </div>
                  </div>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    @click="clearSelectedStudent"
                  >
                    <feather-icon icon="XIcon" size="14" />
                  </b-button>
                </div>
              </div>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Age" label-for="age" required>
              <b-form-input
                v-model.number="form.age"
                type="number"
                min="1"
                max="100"
                placeholder="e.g., 15 *"
                required
                  class="input-compact"
              />
            </b-form-group>
          </b-col>
        </b-row>
        </div>

        <div class="form-card soft-card">
        <b-row>
          <b-col md="3">
            <b-form-group label="Height" label-for="height" required>
                <b-input-group class="unit-input-group compact-input-group">
                <b-form-input
                  v-model.number="form.height"
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="Height *"
                  required
                    class="input-compact"
                />
                <b-input-group-append>
                  <span class="input-group-text">cm</span>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Weight" label-for="weight" required>
                <b-input-group class="unit-input-group compact-input-group">
                <b-form-input
                  v-model.number="form.weight"
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="Weight *"
                  required
                    class="input-compact"
                />
                <b-input-group-append>
                  <span class="input-group-text">kg</span>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Head Circumference" label-for="headCircumference">
                <b-input-group class="unit-input-group compact-input-group">
                <b-form-input
                  v-model.number="form.headCircumference"
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="Head circumference"
                    class="input-compact"
                />
                <b-input-group-append>
                  <span class="input-group-text">cm</span>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="BMI" label-for="BMI" required>
              <b-form-input
                v-model.number="form.BMI"
                type="number"
                step="0.1"
                placeholder="BMI *"
                required
                disabled
                  class="input-compact"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="3">
            <b-form-group label="Pulse" label-for="pulse" required>
              <b-form-input
                v-model.trim="form.pulse"
                placeholder="e.g., 78 bpm *"
                required
                  class="input-compact"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Blood Pressure" label-for="bloodPf" required>
              <b-form-input
                v-model.trim="form.bloodPf"
                placeholder="e.g., 120/80 mmHg *"
                required
                  class="input-compact"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Blood Group" label-for="bloodGroup" required>
              <v-select
                v-model="form.bloodGroup"
                :options="bloodGroupOptions"
                :clearable="false"
                label="text"
                :reduce="(val) => val.value"
                placeholder="Select blood group *"
                required
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Temperature" label-for="temperature" required>
              <b-form-input
                v-model.trim="form.temperature"
                placeholder="e.g., 37 °C *"
                required
                  class="input-compact"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Respiratory Rate" label-for="respiratoryRate" required>
              <b-form-input
                v-model.trim="form.respiratoryRate"
                placeholder="e.g., 16 breaths/min *"
                required
                  class="input-compact"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Health Status" label-for="healthStatus" required>
              <b-form-input
                v-model.trim="form.healthStatus"
                placeholder="e.g., Fit for school activities *"
                required
                  class="input-compact"
              />
            </b-form-group>
          </b-col>
        </b-row>
        </div>

        <hr class="my-2">

        <div class="form-card soft-card">
        <b-row>
          <b-col md="6">
            <b-form-group label="Vaccination" label-for="vaccination">
              <b-form-textarea
                v-model.trim="form.vaccination"
                placeholder="e.g., Hepatitis B, Polio, MMR"
                rows="2"
                  class="input-compact"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Allergy" label-for="allergy">
              <b-form-textarea
                v-model.trim="form.allergy"
                placeholder="e.g., Peanuts, Dust, etc."
                rows="2"
                  class="input-compact"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Medical History" label-for="medicalHistory">
              <b-form-textarea
                v-model.trim="form.medicalHistory"
                placeholder="e.g., Asthma since childhood"
                rows="2"
                  class="input-compact"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Examination" label-for="examination">
              <b-form-textarea
                v-model.trim="form.examination"
                placeholder="e.g., Normal chest sounds, no visible issues"
                rows="2"
                  class="input-compact"
              />
            </b-form-group>
          </b-col>
        </b-row>
        </div>
      </b-form>
      
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false">
          Cancel
        </b-button>
        <b-button variant="primary" @click="saveAssessment" :disabled="loading">
          <b-spinner v-if="loading" small />
          {{ isEdit ? "Update" : "Save" }}
        </b-button>
      </template>
    </b-modal>

    <!-- View Details Modal -->
    <b-modal
      v-model="showViewModal"
      title="Health Assessment Details"
      centered
      size="lg"
      body-class="p-3"
      modal-class="view-details-modal"
    >
      <div v-if="selectedItem" class="view-modal-content">
        <!-- Top info card -->
        <div class="view-info-card">
          <div class="info-left">
            <div class="info-name">
              <feather-icon icon="UserIcon" size="18" class="mr-50 text-primary" />
              <div>
                <h5 class="mb-25">{{ selectedItem.studentName || 'N/A' }}</h5>
                <small class="text-muted d-block">
                  ID: {{ selectedItem.sID }} · Class: {{ selectedItem.className || 'N/A' }} · Age: {{ selectedItem.age || 'N/A' }} years
                </small>
            </div>
            </div>
          </div>
          <div class="info-right">
            <b-badge :variant="getHealthStatusVariant(selectedItem.healthStatus)" class="status-chip">
              {{ selectedItem.healthStatus || 'N/A' }}
              </b-badge>
            <div class="date-time text-right">
              <small class="text-muted d-block">Date: {{ selectedItem.assessmentDate || 'N/A' }}</small>
              <small class="text-muted d-block">Time: {{ formatTime(selectedItem.assessmentTime) }}</small>
            </div>
          </div>
        </div>

        <!-- Physical measurements -->
        <div class="view-section">
          <div class="section-header">
            <h6 class="mb-0">Physical Measurements</h6>
              </div>
          <div class="metrics-grid four-cols">
            <div class="metric-card">
              <span class="metric-label">Height</span>
              <span class="metric-value">{{ selectedItem.height || 'N/A' }}</span>
            </div>
            <div class="metric-card">
              <span class="metric-label">Weight</span>
              <span class="metric-value">{{ selectedItem.weight || 'N/A' }}</span>
              </div>
            <div class="metric-card">
              <span class="metric-label">BMI</span>
              <span class="metric-value">{{ selectedItem.BMI || 'N/A' }}</span>
            </div>
            <div class="metric-card">
              <span class="metric-label">Head Circumference</span>
              <span class="metric-value">{{ selectedItem.headCircumference || 'N/A' }}</span>
            </div>
          </div>
        </div>

        <!-- Vitals -->
        <div class="view-section">
          <div class="section-header">
            <h6 class="mb-0">Vital Signs</h6>
              </div>
          <div class="metrics-grid four-cols">
            <div class="metric-card">
              <span class="metric-label">Pulse</span>
              <span class="metric-value">{{ selectedItem.pulse || 'N/A' }}</span>
            </div>
            <div class="metric-card">
              <span class="metric-label">BP</span>
              <span class="metric-value">{{ selectedItem.bloodPf || 'N/A' }}</span>
              </div>
            <div class="metric-card">
              <span class="metric-label">RR</span>
              <span class="metric-value">{{ selectedItem.respiratoryRate || 'N/A' }}</span>
            </div>
            <div class="metric-card">
              <span class="metric-label">Temp</span>
              <span class="metric-value">{{ selectedItem.temperature || 'N/A' }}</span>
              </div>
            </div>
          <div class="metrics-grid two-cols mt-1">
            <div class="metric-card">
              <span class="metric-label">Blood Group</span>
              <span class="metric-value">{{ selectedItem.bloodGroup || 'N/A' }}</span>
              </div>
            <div class="metric-card">
              <span class="metric-label">Off Days</span>
              <span class="metric-value">{{ selectedItem.offdays || 'N/A' }}</span>
            </div>
          </div>
        </div>

        <!-- Medical info -->
        <div class="view-section">
          <div class="section-header">
            <h6 class="mb-0">Medical Information</h6>
              </div>
          <div class="info-grid">
            <div class="info-card">
              <span class="metric-label">Vaccination</span>
              <p class="info-text">{{ selectedItem.vaccination || 'Not specified' }}</p>
            </div>
            <div class="info-card">
              <span class="metric-label">Allergy</span>
              <p class="info-text">{{ selectedItem.allergy || 'None' }}</p>
              </div>
            <div class="info-card">
              <span class="metric-label">Medical History</span>
              <p class="info-text">{{ selectedItem.medicalHistory || 'None' }}</p>
            </div>
            <div class="info-card">
              <span class="metric-label">Examination</span>
              <p class="info-text">{{ selectedItem.examination || 'Not specified' }}</p>
          </div>
          </div>
        </div>
      </div>
      
      <template #modal-footer>
        <b-button variant="outline-secondary" size="sm" @click="showViewModal = false">
          Close
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BSpinner,
  BFormTextarea,
  BBadge,
  BInputGroup,
  BInputGroupAppend,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { mapActions } from "vuex";
import axios from "axios";

export default {
  name: "HealthAssessment",
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BSpinner,
    BFormTextarea,
    BBadge,
    BInputGroup,
    BInputGroupAppend,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      healthAssessments: [],
      filteredAssessments: [],
      students: [],
      studentOptions: [],
      studentLoading: false,
      // Modal-specific data
      modalStudentOptions: [],
      allStudents: [],
      searchQuery: "",
      searchTimeout: null,
      // New search functionality
      studentSearchQuery: "",
      filteredStudentOptions: [],
      studentSearchLoading: false,
      studentSearchCache: {},
      lastStudentSearchTerm: "",
      studentSearchTimer: null,
      lastStudentSearchToken: 0,
      actionLoadingId: null,
      actionLoadingType: "",
      showModal: false,
      showViewModal: false,
      selectedItem: null,
      isEdit: false,
      loading: false,
      dataLoading: false,
      currentPage: 1,
      perPage: 30,
      totalCount: 0,
      showPagination: true,
      pageSizeOptions: [
        { label: '10', value: 10 },
        { label: '20', value: 20 },
        { label: '30', value: 30 },
        { label: '50', value: 50 },
        { label: '100', value: 100 }
      ],
      // Filter properties
      filters: {
        studentID: null,
        dateRange: '',
        fromDate: '',
        toDate: '',
        search: ''
      },
        fields: [
          { key: "sno", label: "S.No", sortable: false, thStyle: { width: '50px' } },
          { key: "assessmentDate", label: "Date", sortable: false, thStyle: { width: '130px' } },
          { key: "assessmentTime", label: "Time", sortable: false, thStyle: { width: '80px' } },
          { key: "studentName", label: "Student Name", sortable: false, thStyle: { width: '200px' } },
          { key: "age", label: "Age", thStyle: { width: '60px' } },
          { key: "height", label: "Height", thStyle: { width: '80px' } },
          { key: "weight", label: "Weight", thStyle: { width: '80px' } },
          { key: "headCircumference", label: "Head Circumference", thStyle: { width: '140px' } },
          { key: "BMI", label: "BMI", sortable: false, thStyle: { width: '60px' } },
          { key: "pulse", label: "Pulse", thStyle: { width: '70px' } },
          { key: "bloodPf", label: "Blood Pressure", thStyle: { width: '120px' } },
          { key: "bloodGroup", label: "Blood Group", thStyle: { width: '100px' } },
          { key: "respiratoryRate", label: "Respiratory Rate", thStyle: { width: '120px' } },
          { key: "temperature", label: "Temperature", thStyle: { width: '100px' } },
          { key: "healthStatus", label: "Health Status", sortable: false, thStyle: { width: '120px' } },
          { key: "actions", label: "Actions", thStyle: { width: '140px' }, tdClass: 'actions-cell text-center' },
        ],
      form: {
        id: 0,
        sID: null,
        studentName: "",
        age: 0,
        height: "",
        weight: "",
        headCircumference: "",
        BMI: null,
        pulse: "",
        bloodPf: "",
        bloodGroup: "",
        respiratoryRate: "",
        temperature: "",
        vaccination: "",
        allergy: "",
        medicalHistory: "",
        examination: "",
        healthStatus: "",
        assessmentDate: this.getCurrentDate(),
        assessmentTime: this.getCurrentTime(),
        campusID: this.$store.state.userData.cId,
        createdBy: this.$store.state.userData.userID,
        updatedBy: this.$store.state.userData.userID,
      },
      bloodGroupOptions: [
        { text: "A+", value: "A+" },
        { text: "A-", value: "A-" },
        { text: "B+", value: "B+" },
        { text: "B-", value: "B-" },
        { text: "AB+", value: "AB+" },
        { text: "AB-", value: "AB-" },
        { text: "O+", value: "O+" },
        { text: "O-", value: "O-" },
        { text: "Not Shared", value: "Not Shared" },
      ],
      dateConfig: {
        dateFormat: "Y-m-d",
        allowInput: true,
        clickOpens: true,
      },
      timeConfig: {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i:S",
        allowInput: true,
        clickOpens: true,
      },
      dateRangeConfig: {
        mode: "range",
        dateFormat: "Y-m-d",
        allowInput: true,
        clickOpens: true,
        placeholder: "Select date range",
      },
    };
  },
  created() {
    // Initialize rights from menu and enforce view permission
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
          assignSections: ch.assignSections,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    this.loadHealthAssessments();
    this.loadStudents();
  },
  mounted() {
    this.$nextTick(() => {
      this.updateScrollbarWidth();
    });
    window.addEventListener("resize", this.updateScrollbarWidth);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScrollbarWidth);
  },
  computed: {
    hasActiveFilters() {
      return !!(this.filters.dateRange || this.filters.fromDate || this.filters.toDate || this.searchQuery);
    },
    displayedRange() {
      if (this.totalCount === 0) {
        return { start: 0, end: 0, total: 0 };
      }
      const start = Math.min((this.currentPage - 1) * this.perPage + 1, this.totalCount);
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end, total: this.totalCount };
    },
    tableBusy() {
      return this.dataLoading;
    },
    tableEmptyText() {
      if (this.dataLoading) return 'Loading...';
      if (this.hasActiveFilters) return 'No health assessments found matching your filters';
      return 'No health assessments found';
    },
  },
  watch: {
    'form.height': {
      handler() {
        this.calculateBMI();
      },
    },
    'form.weight': {
      handler() {
        this.calculateBMI();
      },
    },
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    getCurrentTime() {
      const now = new Date();
      return now.toTimeString().split(' ')[0]; // Format: HH:mm:ss
    },

    getCurrentDate() {
      const now = new Date();
      return now.toISOString().split('T')[0]; // Format: yyyy-MM-dd
    },


    async loadStudents() {
      this.studentLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Students/LoadDataPagination?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&clsID=0&section=0&page=1&status=present",
          token: this.$store.state.userData.token,
        };
        console.log("Loading students with URL:", obj.url);
        const response = await this.get(obj);
        console.log("Main students response:", response);
        
        // Handle different response structures
        let studentsData = [];
        if (response && response.data && Array.isArray(response.data.data)) {
          studentsData = response.data.data;
          console.log("Using response.data.data structure");
        } else if (response && Array.isArray(response.data)) {
          studentsData = response.data;
          console.log("Using response.data structure");
        } else if (Array.isArray(response)) {
          studentsData = response;
          console.log("Using direct response structure");
        } else {
          console.log("No valid data structure found in response");
        }
        
        console.log("Extracted students data:", studentsData);
        
        this.students = studentsData.map((student) => ({
          id: student.id,
          name: student.name || 'Unknown Student',
        }));
        this.studentOptions = studentsData.map(student => {
          // Parse meta_class field to extract class and section
          let className = 'No Class';
          let sectionName = '';
          
          if (student.meta_class) {
            // Split by " - " to separate class and section
            const parts = student.meta_class.split(' - ');
            if (parts.length >= 2) {
              className = parts[0].trim(); // e.g., "KG-2"
              sectionName = parts[1].trim(); // e.g., "Tulips"
            } else if (parts.length === 1) {
              className = parts[0].trim();
            }
          }
          
          return {
            ...student,
            name: student.name || 'Unknown Student',
            className: className,
            sectionName: sectionName,
            grNo: student.grNo || '',
          };
        });
        
        console.log("Final student options:", this.studentOptions);
        console.log("Student options length:", this.studentOptions.length);
      } catch (error) {
        console.error("Main student load error:", error);
        this.students = [];
        this.studentOptions = [];
      } finally {
        this.studentLoading = false;
      }
    },

    buildHealthQuery(page = this.currentPage, pageSize = this.perPage) {
        const queryParams = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
        page,
        pageSize,
      });

      if (this.filters.studentID) queryParams.append("studentID", this.filters.studentID);
      if (this.filters.fromDate) queryParams.append("fromDate", this.filters.fromDate);
      if (this.filters.toDate) queryParams.append("toDate", this.filters.toDate);
      if (this.filters.search) queryParams.append("search", this.filters.search);

      return queryParams.toString();
    },

    async loadHealthAssessments() {
      this.dataLoading = true;
      
      try {
        const query = this.buildHealthQuery(this.currentPage, this.perPage);
        const obj = {
          url: `${this.$store.state.domain}HealthAssessment/GetByPagination?${query}`,
          token: this.$store.state.userData.token,
        };
        
        console.log("Loading health assessments with URL:", obj.url);
        const response = await axios.get(obj.url, {
          headers: {
            Authorization: `Bearer ${obj.token}`,
          },
        });
        console.log("Health assessments API response:", response.data);
        
        // Handle the API response (flexible parsing, including unwrapped payload)
        const payload = response?.data ?? response ?? {};
        // Extract data and pagination meta from the API (flexible to different shapes)
        // Normalize payload shapes:
        // 1) { status, data: { data: [...], totalCount, page, pageSize, totalPages } }
        // 2) { status, data: [...], totalCount, page, pageSize, totalPages }
        // 3) Direct array [...]
        const container = payload?.data;
        let assessmentsData = [];
        if (Array.isArray(container?.data)) {
          assessmentsData = container.data;
        } else if (Array.isArray(container)) {
          assessmentsData = container;
        } else if (Array.isArray(payload)) {
          assessmentsData = payload;
        }

        // Extract pagination meta safely (numbers)
        const meta = {
          totalCount:
            payload?.totalCount ??
            container?.totalCount ??
            payload?.data?.totalCount ??
            payload?.totalRecords ??
            container?.totalRecords ??
            payload?.data?.totalRecords ??
            null,
          page:
            payload?.page ??
            container?.page ??
            payload?.data?.page ??
            null,
          pageSize:
            payload?.pageSize ??
            container?.pageSize ??
            payload?.data?.pageSize ??
            null,
          totalPages:
            payload?.totalPages ??
            container?.totalPages ??
            payload?.data?.totalPages ??
            null,
        };

        const pageSizeFromApi = Number(meta.pageSize) || this.perPage;
        const totalRecords =
          Number(meta.totalCount) ||
          (meta.totalPages && pageSizeFromApi ? Number(meta.totalPages) * pageSizeFromApi : null) ||
          assessmentsData.length;
        const totalPages =
          Number(meta.totalPages) ||
          Math.ceil((totalRecords || assessmentsData.length) / (pageSizeFromApi || this.perPage));
        const pageFromApi = Number(meta.page) || this.currentPage || 1;

        console.log("Parsed totalRecords:", totalRecords, "totalPages:", totalPages, "pageSize:", pageSizeFromApi);
        
        console.log("Extracted assessments data:", assessmentsData);
        console.log("Total records:", totalRecords, "Total pages:", totalPages);
        
        // Process the data to ensure all required fields are present
        const processedAssessments = await Promise.all(assessmentsData.map(async (assessment) => {
          // Use className directly from API response if available
          let className = assessment.className || 'No Class';
          let studentName = assessment.studentName || 'Unknown Student';
          
          // If className is not provided in API response, try to get it from student search
          if (!assessment.className || assessment.className === 'No Class') {
            try {
              // Call student search API to get proper class information
              const studentSearchObj = {
                url: `${this.$store.state.domain}Students/Search?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&keyword=${encodeURIComponent(studentName)}&type=`,
                token: this.$store.state.userData.token,
              };
              
              const studentResponse = await this.get(studentSearchObj);
              if (studentResponse && studentResponse.data && Array.isArray(studentResponse.data)) {
                const studentData = studentResponse.data.find(s => s.id === (assessment.sID || assessment.studentID));
                if (studentData) {
                  studentName = studentData.name || studentName;
                  className = (studentData.cls_current && studentData.cls_current.name) || 'No Class';
                }
              }
            } catch (error) {
              console.log("Could not fetch student details, using fallback");
              // Fallback: try to extract class from student name if it contains class info
              if (studentName.includes(' - ')) {
                const nameParts = studentName.split(' - ');
                if (nameParts.length === 2) {
                  studentName = nameParts[0].trim();
                  className = nameParts[1].trim();
                }
              }
            }
          }
          
          return {
            id: assessment.id,
            sID: assessment.sID || assessment.studentID,
            studentName: studentName,
            className: className,
            age: assessment.age || 0,
            height: assessment.height || 'N/A',
            weight: assessment.weight || 'N/A',
            headCircumference: assessment.headCircumference || assessment.headCircumferenceCm || 'N/A',
            BMI: assessment.BMI || assessment.bmi || 0,
            pulse: assessment.pulse || 'N/A',
            bloodPf: assessment.bloodPf || assessment.bloodPressure || 'N/A',
            bloodGroup: assessment.bloodGroup || 'N/A',
            respiratoryRate: assessment.respiratoryRate || 'N/A',
            temperature: assessment.temperature || 'N/A',
            vaccination: assessment.vaccination || '',
            allergy: assessment.allergy || '',
            medicalHistory: assessment.medicalHistory || '',
            examination: assessment.examination || '',
            healthStatus: assessment.healthStatus || 'Not assessed',
            assessmentDate: assessment.assessmentDate,
            assessmentTime: assessment.assessmentTime,
            createdOn: assessment.createdOn,
            updatedOn: assessment.updatedOn,
            createdBy: assessment.createdBy,
            updatedBy: assessment.updatedBy
          };
        }));
        
        // Sort by date in descending order (latest first)
        this.healthAssessments = processedAssessments.sort((a, b) => {
          const dateA = new Date(a.assessmentDate ? a.assessmentDate : 0);
          const dateB = new Date(b.assessmentDate ? b.assessmentDate : 0);
          return dateB - dateA; // Descending order (latest first)
        });
        
        this.filteredAssessments = [...this.healthAssessments];
        this.totalCount = totalRecords;
        this.totalPages = totalPages || Math.ceil(totalRecords / (pageSizeFromApi || this.perPage));
        this.perPage = pageSizeFromApi || this.perPage;
        this.currentPage = pageFromApi || this.currentPage || 1;
        // Keep currentPage within bounds if API reduces total pages
        if (this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages || 1;
        }
        
        console.log("Processed health assessments:", this.healthAssessments);
        console.log("Total count set to:", this.totalCount);
        console.log("Applied filters:", this.filters);
        
      } catch (error) {
        console.error("Error loading health assessments:", error);
        this.$bvToast.toast("Failed to load health assessments", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        
        // Set empty data on error
        this.healthAssessments = [];
        this.filteredAssessments = [];
        this.totalCount = 0;
      } finally {
      this.dataLoading = false;
      }
    },

    onSearchChange() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      // Debounce search to avoid too many API calls
      this.searchTimeout = setTimeout(() => {
        this.filters.search = this.searchQuery;
        this.currentPage = 1; // Reset to first page when searching
        this.loadHealthAssessments();
      }, 500); // 500ms debounce
    },

    // Method to apply filters and reload data
    applyFilters() {
      this.currentPage = 1; // Reset to first page when applying filters
      this.loadHealthAssessments();
    },

    // Method to handle date range change
    onDateRangeChange(selectedDates) {
      if (selectedDates && selectedDates.length === 2) {
        this.filters.fromDate = selectedDates[0];
        this.filters.toDate = selectedDates[1];
      } else {
        this.filters.fromDate = '';
        this.filters.toDate = '';
      }
      this.applyFilters();
    },

    // Method to clear all filters
    clearFilters() {
      this.filters = {
        studentID: null,
        dateRange: '',
        fromDate: '',
        toDate: '',
        search: ''
      };
      this.searchQuery = '';
      this.currentPage = 1;
      this.loadHealthAssessments();
    },

    // Pagination methods
    onPageSizeChange() {
      this.currentPage = 1;
      this.loadHealthAssessments();
    },

    closePageSizeDropdown() {
      // Close the v-select dropdown after selecting an option
      if (this.$refs.pageSizeSelect && this.$refs.pageSizeSelect.toggleDropdown) {
        this.$refs.pageSizeSelect.toggleDropdown(false);
      }
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadHealthAssessments();
    },

    // Sync scroll between top/bottom scrollbars and table wrapper
    syncScroll(event) {
      const tableWrapper = this.$refs.tableWrapper;
      const topScrollbar = this.$refs.topScrollbar;
      const bottomScrollbar = this.$refs.bottomScrollbar;
      if (!tableWrapper || !topScrollbar || !bottomScrollbar) return;

      const target = event && event.target;
      if (target === tableWrapper) {
        topScrollbar.scrollLeft = tableWrapper.scrollLeft;
        bottomScrollbar.scrollLeft = tableWrapper.scrollLeft;
      } else if (target === topScrollbar) {
        tableWrapper.scrollLeft = topScrollbar.scrollLeft;
        bottomScrollbar.scrollLeft = topScrollbar.scrollLeft;
      } else if (target === bottomScrollbar) {
        tableWrapper.scrollLeft = bottomScrollbar.scrollLeft;
        topScrollbar.scrollLeft = bottomScrollbar.scrollLeft;
      }
    },

    updateScrollbarWidth() {
      this.$nextTick(() => {
        const tableWrapper = this.$refs.tableWrapper;
        const topScrollbar = this.$refs.topScrollbar;
        const bottomScrollbar = this.$refs.bottomScrollbar;
        const topContent = topScrollbar && topScrollbar.querySelector(".table-scrollbar-content");
        const bottomContent = bottomScrollbar && bottomScrollbar.querySelector(".table-scrollbar-content");
        if (tableWrapper && topContent) {
          topContent.style.width = `${tableWrapper.scrollWidth}px`;
          topScrollbar.scrollLeft = tableWrapper.scrollLeft;
        }
        if (tableWrapper && bottomContent) {
          bottomContent.style.width = `${tableWrapper.scrollWidth}px`;
          bottomScrollbar.scrollLeft = tableWrapper.scrollLeft;
        }
      });
    },

    openAddModal() {
      this.isEdit = false;
      this.resetModalFilters();
      this.loadModalStudents();
      
      // Update time to current time when opening modal
      this.form.assessmentDate = this.getCurrentDate();
      this.form.assessmentTime = this.getCurrentTime();
      
      this.showModal = true;
    },

    async openPrintModal() {
      // Fetch all pages for print to ensure complete data
      try {
        const allData = await this.fetchAllAssessmentsForPrint();
        if (allData && Array.isArray(allData) && allData.length > 0) {
          this.filteredAssessments = allData;
        }
      } catch (err) {
        console.error("Error fetching all data for print:", err);
      }
      this.printHealthAssessmentReport();
    },

    async fetchAllAssessmentsForPrint() {
      try {
        // If totalCount known, request in one go with large pageSize; fallback to current perPage
        const pageSize = this.totalCount > 0 ? this.totalCount : Math.max(this.perPage, 500);
        const query = this.buildHealthQuery(1, pageSize);
        const obj = {
          url: `${this.$store.state.domain}HealthAssessment/GetByPagination?${query}`,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);

        let assessmentsData = [];
        if (response && response.data && Array.isArray(response.data.data)) {
          assessmentsData = response.data.data;
        } else if (response && Array.isArray(response.data)) {
          assessmentsData = response.data;
        } else if (Array.isArray(response)) {
          assessmentsData = response;
        }

        // Map/process same as loadHealthAssessments
        const processed = await Promise.all(
          (assessmentsData || []).map(async (assessment) => {
            let className = assessment.className || "No Class";
            let studentName = assessment.studentName || "Unknown Student";

            if (!assessment.className || assessment.className === "No Class") {
              try {
                const studentSearchObj = {
                  url: `${this.$store.state.domain}Students/Search?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&keyword=${encodeURIComponent(studentName)}&type=`,
                  token: this.$store.state.userData.token,
                };

                const studentResponse = await this.get(studentSearchObj);
                if (studentResponse && studentResponse.data && Array.isArray(studentResponse.data)) {
                  const studentData = studentResponse.data.find(
                    (s) => s.id === (assessment.sID || assessment.studentID)
                  );
                  if (studentData) {
                    studentName = studentData.name || studentName;
                    className = (studentData.cls_current && studentData.cls_current.name) || "No Class";
                  }
                }
              } catch (error) {
                if (studentName.includes(" - ")) {
                  const nameParts = studentName.split(" - ");
                  if (nameParts.length === 2) {
                    studentName = nameParts[0].trim();
                    className = nameParts[1].trim();
                  }
                }
              }
            }

            return {
              id: assessment.id,
              sID: assessment.sID || assessment.studentID,
              studentName: studentName,
              className: className,
              age: assessment.age || 0,
              height: assessment.height || "N/A",
              weight: assessment.weight || "N/A",
              headCircumference: assessment.headCircumference || assessment.headCircumferenceCm || "N/A",
              BMI: assessment.BMI || assessment.bmi || 0,
              pulse: assessment.pulse || "N/A",
              bloodPf: assessment.bloodPf || assessment.bloodPressure || "N/A",
              bloodGroup: assessment.bloodGroup || "N/A",
              respiratoryRate: assessment.respiratoryRate || "N/A",
              temperature: assessment.temperature || "N/A",
              vaccination: assessment.vaccination || "",
              allergy: assessment.allergy || "",
              medicalHistory: assessment.medicalHistory || "",
              examination: assessment.examination || "",
              healthStatus: assessment.healthStatus || "Not assessed",
              assessmentDate: assessment.assessmentDate,
              assessmentTime: assessment.assessmentTime,
              createdOn: assessment.createdOn,
              updatedOn: assessment.updatedOn,
              createdBy: assessment.createdBy,
              updatedBy: assessment.updatedBy
            };
          })
        );

        return processed;
      } catch (error) {
        console.error("Error fetching all assessments for print:", error);
        return this.filteredAssessments || [];
      }
    },

    getActionId(item) {
      return item?.id ?? item?.sID ?? item?.studentID ?? item?.med?.id ?? null;
    },

    isActionLoading(item, type) {
      return this.actionLoadingId === this.getActionId(item) && this.actionLoadingType === type;
    },

    viewDetails(item) {
      this.actionLoadingId = this.getActionId(item);
      this.actionLoadingType = "view";
      try {
        this.selectedItem = item;
        this.showViewModal = true;
      } finally {
        this.actionLoadingId = null;
        this.actionLoadingType = "";
      }
    },

    async editAssessment(item) {
      this.actionLoadingId = this.getActionId(item);
      this.actionLoadingType = "edit";
      this.isEdit = true;
      this.form = { ...item };
      
      // Clean up empty fields to avoid showing "Not specified" or "None"
      this.form.vaccination = this.form.vaccination === 'none' || this.form.vaccination === 'Not specified' ? '' : this.form.vaccination;
      this.form.allergy = this.form.allergy === 'none' || this.form.allergy === 'None' ? '' : this.form.allergy;
      this.form.medicalHistory = this.form.medicalHistory === 'none' || this.form.medicalHistory === 'None' ? '' : this.form.medicalHistory;
      this.form.examination = this.form.examination === 'Not specified' || this.form.examination === 'none' ? '' : this.form.examination;
      
      // Ensure date and time are properly formatted for editing
      if (!this.form.assessmentDate) {
        this.form.assessmentDate = this.getCurrentDate();
      }
      if (!this.form.assessmentTime) {
        this.form.assessmentTime = this.getCurrentTime();
      }
      
      // Load the specific student data for the dropdown
      await this.loadStudentForEdit(item.sID);
      
      this.showModal = true;
      this.actionLoadingId = null;
      this.actionLoadingType = "";
    },

    async deleteAssessment(item) {
      if (!this.rights?.delete) {
        this.$bvToast.toast("You don't have permission to delete.", {
          title: "Not Allowed",
          variant: "danger",
          solid: true,
        });
        return;
      }

      const recordId = this.getActionId(item);
      if (!recordId) {
        this.$bvToast.toast("Unable to delete: missing record id.", {
          title: "Delete Failed",
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.actionLoadingId = recordId;
      this.actionLoadingType = "delete";
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        const obj = {
          url: `${this.$store.state.domain}HealthAssessment/Delete?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&id=${recordId}`,
          message: "Health assessment deleted successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };
        
        console.log("Deleting health assessment:", recordId);
        console.log("Delete URL:", obj.url);
        
        const success = await this.delete(obj);
        console.log("Delete success:", success);
        
        if (success) {
          this.loadHealthAssessments();
        }
      }
      this.actionLoadingId = null;
      this.actionLoadingType = "";
    },

    async saveAssessment() {
      // Validate required fields
      const requiredFields = [
        { field: 'sID', label: 'Student' },
        { field: 'age', label: 'Age' },
        { field: 'height', label: 'Height' },
        { field: 'weight', label: 'Weight' },
        { field: 'BMI', label: 'BMI' },
        { field: 'pulse', label: 'Pulse' },
        { field: 'bloodPf', label: 'Blood Pressure' },
        { field: 'bloodGroup', label: 'Blood Group' },
        { field: 'respiratoryRate', label: 'Respiratory Rate' },
        { field: 'temperature', label: 'Temperature' },
        { field: 'healthStatus', label: 'Health Status' }
      ];

      const missingFields = requiredFields.filter(field => {
        const value = this.form[field.field];
        return !value || (typeof value === 'string' && value.trim() === '') || (typeof value === 'number' && value <= 0);
      });

      if (missingFields.length > 0) {
        const fieldNames = missingFields.map(field => field.label).join(', ');
        this.$bvToast.toast(`Please fill the following required fields: ${fieldNames}`, {
          title: "Required Fields Missing!",
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.loading = true;

      try {
      // Prepare form data for save/edit
      const formData = { ...this.form };
      
      // Always use the time at the moment of saving
      formData.assessmentTime = this.getCurrentTime();
      
      // Handle student ID - if sID is an object, extract the id
      if (formData.sID && typeof formData.sID === 'object') {
        formData.studentName = formData.sID.name;
        formData.className = (formData.sID.cls_current && formData.sID.cls_current.name) || 'No Class';
          formData.sID = formData.sID.id; // Extract the actual student ID
      }
      
      // Handle createdOn and updatedOn based on operation type
      if (this.isEdit) {
        // For edit operations
        formData.updatedBy = this.$store.state.userData.userID;
        formData.updatedOn = new Date().toISOString();
        // Keep existing createdOn, don't modify it
      } else {
        // For new records
        formData.createdOn = new Date().toISOString();
        formData.updatedOn = null;
        // Remove updatedBy for new records
        delete formData.updatedBy;
      }

        // Convert numeric measurement fields to strings for API compatibility
        const heightString =
          formData.height !== undefined && formData.height !== null && formData.height !== ""
            ? String(formData.height)
            : "";
        const weightString =
          formData.weight !== undefined && formData.weight !== null && formData.weight !== ""
            ? String(formData.weight)
            : "";
        const headCircumferenceString =
          formData.headCircumference !== undefined &&
          formData.headCircumference !== null &&
          formData.headCircumference !== ""
            ? String(formData.headCircumference)
            : "";

        // Call the actual API - single Save endpoint for both create and update
        const apiUrl = `${this.$store.state.domain}HealthAssessment/Save?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&userID=${this.$store.state.userData.userID}`;
        
        // Prepare the request body according to HealthAssessmentDto structure
        const requestBody = {
          id: this.isEdit ? formData.id : 0, // 0 for new record, >0 for update
          studentName: formData.studentName,
          sID: String(formData.sID), // Convert to string as API expects
          age: formData.age || 0,
          height: heightString,
          weight: weightString,
          BMI: formData.BMI,
          pulse: formData.pulse,
          bloodPf: formData.bloodPf,
          bloodGroup: formData.bloodGroup,
          respiratoryRate: formData.respiratoryRate,
          temperature: formData.temperature,
          headCircumference: headCircumferenceString,
          vaccination: formData.vaccination,
          allergy: formData.allergy,
          medicalHistory: formData.medicalHistory,
          examination: formData.examination,
          healthStatus: formData.healthStatus,
          assessmentDate: formData.assessmentDate || new Date().toISOString().split('T')[0], // Format: yyyy-MM-dd
          assessmentTime: formData.assessmentTime || new Date().toTimeString().split(' ')[0], // Format: HH:mm:ss
          campusID: this.$store.state.userData.cId,
          studentID: parseInt(formData.sID) || 0 // Convert to integer for studentID
        };
        
        const obj = {
          url: apiUrl,
          body: requestBody,
          token: this.$store.state.userData.token,
          message: "Health assessment " + (this.isEdit ? "updated" : "created") + " successfully.",
          context: this,
        };
        
        // Additional validation for request body
        if (!requestBody.studentName || !requestBody.sID || !requestBody.healthStatus) {
          this.$bvToast.toast("Student information is missing. Please select a student.", {
            title: "Student Required!",
            variant: "danger",
            solid: true,
          });
          return;
        }
        
        console.log("Saving health assessment - Request Body:", JSON.stringify(requestBody, null, 2));
        console.log("API URL:", apiUrl);
        console.log("Request Object:", obj);
        
        const success = await this.post(obj);
        console.log("Save success:", success);
        
        if (success) {
        this.showModal = false;
        this.loadHealthAssessments();
        }
        
      } catch (error) {
        console.error("Error saving health assessment:", error);
        this.$bvToast.toast("Failed to save health assessment", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        id: 0,
        sID: null,
        studentName: "",
        age: 0,
        height: "",
        weight: "",
        BMI: null,
        pulse: "",
        bloodPf: "",
        bloodGroup: "",
        respiratoryRate: "",
        temperature: "",
        vaccination: "",
        allergy: "",
        medicalHistory: "",
        examination: "",
        healthStatus: "",
        assessmentDate: this.getCurrentDate(),
        assessmentTime: this.getCurrentTime(),
        campusID: this.$store.state.userData.cId,
        createdBy: this.$store.state.userData.userID,
        updatedBy: this.$store.state.userData.userID,
      };
      this.resetModalFilters();
    },

    resetModalFilters() {
      this.modalStudentOptions = [];
      this.allStudents = [];
    },

    async loadModalStudents() {
      // Load some default students to show the dropdown is working
      try {
        const obj = {
          url: `${this.$store.state.domain}Students/LoadDataPagination?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&clsID=0&section=0&page=1&status=present`,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        
        let studentsData = [];
        if (response && response.data && Array.isArray(response.data.data)) {
          studentsData = response.data.data.slice(0, 15); // Get first 15 students as default
        } else if (response && Array.isArray(response.data)) {
          studentsData = response.data.slice(0, 15);
        } else if (Array.isArray(response)) {
          studentsData = response.slice(0, 15);
        }
        
        this.modalStudentOptions = studentsData.map(student => {
          // Parse meta_class field to extract class and section
          let className = 'No Class';
          let sectionName = '';
          
          if (student.meta_class) {
            // Split by " - " to separate class and section
            const parts = student.meta_class.split(' - ');
            if (parts.length >= 2) {
              className = parts[0].trim(); // e.g., "KG-2"
              sectionName = parts[1].trim(); // e.g., "Tulips"
            } else if (parts.length === 1) {
              className = parts[0].trim();
            }
          }
          
          return {
            ...student,
            displayName: student.name || 'Unknown Student',
            name: student.name || 'Unknown Student',
            className: className,
            sectionName: sectionName,
            grNo: student.grNo || '',
          };
        });
        
        console.log("Default student options loaded:", this.modalStudentOptions.length);
      } catch (error) {
        console.error("Error loading default students:", error);
        this.modalStudentOptions = [];
      }
    },

    onStudentDropdownOpen() {
      // When dropdown opens, only clear if no search has been performed
      if (this.modalStudentOptions.length === 0) {
        // No search performed yet, keep it empty
        return;
      }
      // If search was performed and results exist, keep them visible
    },

    onStudentSelectionChange(selectedStudent) {
      // If student is cleared (null), reload default students
      if (!selectedStudent) {
        this.loadModalStudents();
      }
    },

    // New search functionality methods
    async onStudentSearchChange() {
      // Debounce to reduce hits
      if (this.studentSearchTimer) clearTimeout(this.studentSearchTimer);
      this.studentSearchTimer = setTimeout(() => {
        this.performStudentSearch();
      }, 700); // increased debounce to reduce API hits
    },

    async performStudentSearch() {
      const term = (this.studentSearchQuery || "").trim().toLowerCase();
      if (term.length < 2) {
        this.filteredStudentOptions = [];
        this.lastStudentSearchTerm = "";
        return;
      }

      // If we already searched this term, use cache to avoid extra API hit
      if (this.studentSearchCache[term]) {
        this.filteredStudentOptions = this.studentSearchCache[term];
        this.lastStudentSearchTerm = term;
        return;
      }

      const token = ++this.lastStudentSearchToken; // track latest call
      this.studentSearchLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}Students/Search?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&keyword=${encodeURIComponent(this.studentSearchQuery)}&type=`,
          token: this.$store.state.userData.token,
        };
        
        console.log("Searching students with URL:", obj.url);
        const response = await this.get(obj);
        // Drop stale responses
        if (token !== this.lastStudentSearchToken) {
          return;
        }
        console.log("Student search response:", response);
        
        // Handle different response structures
        let studentsData = [];
        if (response && response.data && Array.isArray(response.data)) {
          studentsData = response.data;
        } else if (Array.isArray(response)) {
          studentsData = response;
        }
        
        // Map students with proper structure for display
        const mapped = studentsData.map(student => {
          // Parse meta_class field to extract class and section
          let className = 'No Class';
          let sectionName = '';
          
          if (student.meta_class) {
            // Split by " - " to separate class and section
            const parts = student.meta_class.split(' - ');
            if (parts.length >= 2) {
              className = parts[0].trim(); // e.g., "KG-2"
              sectionName = parts[1].trim(); // e.g., "Tulips"
            } else if (parts.length === 1) {
              className = parts[0].trim();
            }
          }
          
          return {
            ...student,
            name: student.name || 'Unknown Student',
            className: className,
            sectionName: sectionName,
            grNo: student.grNo || '',
            sections: student.sections || null,
          };
        });

        this.filteredStudentOptions = mapped;
        // Cache results for this term to avoid repeated API calls (elastic search feel)
        this.studentSearchCache[term] = mapped;
        this.lastStudentSearchTerm = term;
        
        console.log("Filtered student options:", this.filteredStudentOptions.length, "students");
      } catch (error) {
        console.error("Student search error:", error);
        this.filteredStudentOptions = [];
      } finally {
        // Only clear loading if this is the latest call
        if (token === this.lastStudentSearchToken) {
        this.studentSearchLoading = false;
        }
      }
    },

    selectStudent(student) {
      this.form.sID = student;
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      
      // Auto-fill age from date of birth
      if (student.date_of_birth) {
        this.form.age = this.calculateAge(student.date_of_birth);
      }
    },

    clearSelectedStudent() {
      this.form.sID = null;
      this.form.age = 0; // Reset age when student is cleared
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
    },

    async loadStudentForEdit(studentID) {
      this.studentLoading = true;
      try {
        // Call the API to get the specific student's health assessment data
        const obj = {
          url: `${this.$store.state.domain}HealthAssessment/GetByPagination?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&studentID=${studentID}&page=1&pageSize=10`,
          token: this.$store.state.userData.token,
        };
        
        console.log("Loading student for edit with URL:", obj.url);
        const response = await this.get(obj);
        console.log("Student health assessment response:", response);
        
        // Handle the response structure
        let studentData = null;
        let assessment = null;
        let studentName = '';
        let studentID = null;
        
        if (response && response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          // Get the first assessment to extract student info
          assessment = response.data.data[0];
          
          // Use className directly from API response
          let className = assessment.className || 'No Class';
          studentName = assessment.studentName || 'Unknown Student';
          studentID = assessment.sID || assessment.studentID;
          
          // Only extract from student name if className is not provided
          if (!assessment.className && studentName.includes(' - ')) {
            const nameParts = studentName.split(' - ');
            if (nameParts.length === 2) {
              studentName = nameParts[0].trim();
              className = nameParts[1].trim();
            }
          }
          
          studentData = {
            id: studentID,
            name: studentName,
            cls_current: { name: className },
            displayName: `${studentName} - ${className}`,
          };
        } else if (response && Array.isArray(response.data) && response.data.length > 0) {
          // Alternative response structure
          assessment = response.data[0];
          
          // Use className directly from API response
          let className = assessment.className || 'No Class';
          studentName = assessment.studentName || 'Unknown Student';
          studentID = assessment.sID || assessment.studentID;
          
          // Only extract from student name if className is not provided
          if (!assessment.className && studentName.includes(' - ')) {
            const nameParts = studentName.split(' - ');
            if (nameParts.length === 2) {
              studentName = nameParts[0].trim();
              className = nameParts[1].trim();
            }
          }
          
          studentData = {
            id: studentID,
            name: studentName,
            cls_current: { name: className },
            displayName: `${studentName} - ${className}`,
          };
        }
        
        if (studentData) {
          // Try to fetch full student details including date_of_birth
          try {
            const studentSearchObj = {
              url: `${this.$store.state.domain}Students/Search?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&keyword=${encodeURIComponent(studentName)}&type=`,
              token: this.$store.state.userData.token,
            };
            const studentResponse = await this.get(studentSearchObj);
            if (studentResponse && studentResponse.data && Array.isArray(studentResponse.data)) {
              const fullStudentData = studentResponse.data.find(s => s.id === studentID);
              if (fullStudentData && fullStudentData.date_of_birth) {
                studentData.date_of_birth = fullStudentData.date_of_birth;
                // Calculate age from date_of_birth if available
                this.form.age = this.calculateAge(fullStudentData.date_of_birth);
              }
            }
          } catch (error) {
            console.log("Could not fetch student date_of_birth, using existing age");
          }
          
          // Set the student in modal options and form
          this.modalStudentOptions = [studentData];
          this.form.sID = studentData;
          console.log("Student loaded for edit:", studentData);
        } else {
          // Fallback: try to get student info from the current item
          console.log("No student data found in API response, using fallback");
          
          // Use className from form if available, otherwise extract from student name
          let className = this.form.className || 'No Class';
          let studentName = this.form.studentName || 'Unknown Student';
          
          // Only extract from student name if className is not available
          if (!this.form.className && studentName.includes(' - ')) {
            const nameParts = studentName.split(' - ');
            if (nameParts.length === 2) {
              studentName = nameParts[0].trim();
              className = nameParts[1].trim();
            }
          }
          
          const fallbackStudent = {
            id: this.form.sID,
            name: studentName,
            cls_current: { name: className },
            displayName: `${studentName} - ${className}`,
          };
          this.modalStudentOptions = [fallbackStudent];
          this.form.sID = fallbackStudent;
        }
        
      } catch (error) {
        console.error("Error loading student for edit:", error);
        // Fallback: create student object from form data
        
        // Use className from form if available, otherwise extract from student name
        let className = this.form.className || 'No Class';
        let studentName = this.form.studentName || 'Unknown Student';
        
        // Only extract from student name if className is not available
        if (!this.form.className && studentName.includes(' - ')) {
          const nameParts = studentName.split(' - ');
          if (nameParts.length === 2) {
            studentName = nameParts[0].trim();
            className = nameParts[1].trim();
          }
        }
        
        const fallbackStudent = {
          id: this.form.sID,
          name: studentName,
          cls_current: { name: className },
          displayName: `${studentName} - ${className}`,
        };
        this.modalStudentOptions = [fallbackStudent];
        this.form.sID = fallbackStudent;
      } finally {
        this.studentLoading = false;
      }
    },

    async searchModalStudents(search) {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      // If search is empty, don't show any students
      if (!search || search.trim() === '') {
        this.modalStudentOptions = [];
        return;
      }
      
      // Debounce search to avoid too many API calls (increased delay for slow typers)
      this.searchTimeout = setTimeout(async () => {
        this.studentLoading = true;
        try {
          const obj = {
            url: `${this.$store.state.domain}Students/Search?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&keyword=${encodeURIComponent(search)}&type=`,
            token: this.$store.state.userData.token,
          };
          console.log("Searching students with URL:", obj.url);
          const response = await this.get(obj);
          console.log("Students search response:", response);
          
          // Handle the response structure from Students/Search
          let studentsData = [];
          if (response && response.data && Array.isArray(response.data)) {
            studentsData = response.data;
          } else if (Array.isArray(response)) {
            studentsData = response;
          }
          
          // Map students with proper structure for v-select
          this.modalStudentOptions = studentsData.map(student => {
            // Parse meta_class field to extract class and section
            let className = 'No Class';
            let sectionName = '';
            
            if (student.meta_class) {
              // Split by " - " to separate class and section
              const parts = student.meta_class.split(' - ');
              if (parts.length >= 2) {
                className = parts[0].trim(); // e.g., "KG-2"
                sectionName = parts[1].trim(); // e.g., "Tulips"
              } else if (parts.length === 1) {
                className = parts[0].trim();
              }
            }
            
            return {
              ...student,
              displayName: student.name || 'Unknown Student',
              name: student.name || 'Unknown Student',
              className: className,
              sectionName: sectionName,
              grNo: student.grNo || '',
            };
          });
          
          console.log("Filtered student options:", this.modalStudentOptions);
          console.log("Modal student options length:", this.modalStudentOptions.length);
          
          // Auto-open dropdown immediately when search results are available
          if (this.modalStudentOptions.length > 0) {
            // Add a small delay to ensure DOM is ready
            setTimeout(() => {
              this.$nextTick(() => {
                if (this.$refs.studentSelect) {
                  // Try multiple methods to open the dropdown
                  try {
                    // Method 1: Try setting open property
                    if (this.$refs.studentSelect.open !== undefined) {
                      this.$refs.studentSelect.open = true;
                    }
                    // Method 2: Try calling the open method if it exists
                    else if (typeof this.$refs.studentSelect.open === 'function') {
                      this.$refs.studentSelect.open();
                    }
                    // Method 3: Trigger click on dropdown toggle
                    else {
                      const dropdownToggle = this.$refs.studentSelect.$el?.querySelector('.vs__dropdown-toggle');
                      if (dropdownToggle) {
                        dropdownToggle.click();
                      }
                    }
                  } catch (error) {
                    console.log('Auto-open dropdown error:', error);
                    // Fallback: try direct DOM manipulation
                    const dropdownToggle = this.$refs.studentSelect.$el?.querySelector('.vs__dropdown-toggle');
                    if (dropdownToggle) {
                      dropdownToggle.click();
                    }
                  }
                }
              });
            }, 100); // 100ms delay to ensure DOM is ready
          }
        } catch (error) {
          console.error("Student search error:", error);
          this.modalStudentOptions = [];
        } finally {
          this.studentLoading = false;
        }
      }, 800); // 800ms debounce for slow typers
    },

    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return 0;
      
      try {
        // Handle different date formats
        let birthDate;
        if (typeof dateOfBirth === 'string') {
          // Handle ISO format: "2015-03-01T00:00:00"
          birthDate = new Date(dateOfBirth);
        } else if (dateOfBirth instanceof Date) {
          birthDate = dateOfBirth;
        } else {
          return 0;
        }
        
        // Check if date is valid
        if (isNaN(birthDate.getTime())) {
          return 0;
        }
        
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        // Adjust age if birthday hasn't occurred this year
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        
        return age > 0 ? age : 0;
      } catch (error) {
        console.error("Error calculating age:", error);
        return 0;
      }
    },

    calculateBMI() {
      // BMI formula: weight (kg) / (height (m))²
      // Since height is in cm, we need to convert: BMI = weight / (height/100)²
      // Which simplifies to: BMI = (weight * 10000) / (height²)
      
      const height = parseFloat(this.form.height);
      const weight = parseFloat(this.form.weight);
      
      // Check if both height and weight are valid numbers
      if (!height || !weight || isNaN(height) || isNaN(weight) || height <= 0 || weight <= 0) {
        this.form.BMI = null;
        return;
      }
      
      // Extract numeric value from height (remove "cm" if present)
      const heightValue = typeof height === 'string' ? parseFloat(height.replace(/[^\d.]/g, '')) : height;
      const weightValue = typeof weight === 'string' ? parseFloat(weight.replace(/[^\d.]/g, '')) : weight;
      
      if (!heightValue || !weightValue || isNaN(heightValue) || isNaN(weightValue) || heightValue <= 0 || weightValue <= 0) {
        this.form.BMI = null;
        return;
      }
      
      // Calculate BMI: weight (kg) / (height (m))²
      // Height is in cm, so convert to meters: height (m) = height (cm) / 100
      const heightInMeters = heightValue / 100;
      const bmi = weightValue / (heightInMeters * heightInMeters);
      
      // Round to 1 decimal place
      this.form.BMI = Math.round(bmi * 10) / 10;
    },

    getBMIVariant(bmi) {
      if (bmi < 18.5) return "light-warning";
      if (bmi >= 18.5 && bmi < 25) return "light-success";
      if (bmi >= 25 && bmi < 30) return "light-warning";
      return "light-danger";
    },

    getHealthStatusVariant(status) {
      switch (status) {
        case "Fit for school activities":
          return "light-success";
        case "Limited activities":
          return "light-warning";
        case "Medical attention required":
          return "light-danger";
        case "Emergency contact needed":
          return "light-danger";
        default:
          return "light-secondary";
      }
    },


    formatTime(time) {
      if (!time || time === 'N/A') return 'N/A';
      
      try {
        // Handle different time formats: "HH:mm:ss" or "HH:mm"
        const timeParts = time.split(':');
        if (timeParts.length < 2) return time; // Return original if format is unexpected
        
        const hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1], 10);
        
        if (isNaN(hours) || isNaN(minutes)) return time;
        
        // Convert to 12-hour format
        let hours12 = hours % 12;
        if (hours12 === 0) hours12 = 12; // 0 should be displayed as 12
        
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const minutesStr = minutes.toString().padStart(2, '0');
        
        return `${hours12}:${minutesStr} ${ampm}`;
      } catch (error) {
        console.error("Error formatting time:", error);
        return time; // Return original time if parsing fails
      }
    },

    formatAssessmentDateTime(date, time) {
      if (!date) return 'N/A';
      
      try {
        const assessmentDate = new Date(date);
        const formattedDate = assessmentDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        
        if (time) {
          return `${formattedDate} at ${this.formatTime(time)}`;
        }
        return formattedDate;
      } catch (error) {
        return date; // Return original date if parsing fails
      }
    },

    printHealthAssessmentReport() {
      console.log("Generating health assessment print report...");
      
      // Create the HTML content for the print view
      const printContent = this.generatePrintableHTML();
      
      // Open new window/tab
      const newWindow = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
      
      if (newWindow) {
        newWindow.document.write(printContent);
        newWindow.document.close();
        
        // Add print functionality after the content is loaded
        newWindow.onload = function() {
          // Add print button functionality
          const printBtn = newWindow.document.getElementById('printButton');
          if (printBtn) {
            printBtn.addEventListener('click', function() {
              newWindow.print();
            });
          }
        };
      } else {
        alert('Pop-up blocked! Please allow pop-ups for this site to view the health assessment report.');
      }
    },
    
    generatePrintableHTML() {
      // Use current table data (filteredAssessments) for printing
      const dataToUse = this.filteredAssessments || [];
      
      // Generate table rows with pagination (max 50 records per page)
      const totalRecords = dataToUse.length;
      console.log("Generating print HTML with records:", totalRecords);
      console.log("Data to use:", dataToUse);
      
      const recordsPerPage = 50;
      const totalPages = Math.ceil(totalRecords / recordsPerPage);
      
      let allTableRows = '';
      
      for (let page = 0; page < totalPages; page++) {
        const startIndex = page * recordsPerPage;
        const endIndex = Math.min(startIndex + recordsPerPage, totalRecords);
        const pageRecords = dataToUse.slice(startIndex, endIndex);
        
        console.log("Page records for page", page, ":", pageRecords);
        
        const tableRows = pageRecords.map((item, index) => {
          console.log("Processing item", index, ":", item);
          const studentName = item.studentName || 'N/A';
          const className = item.className || 'N/A';
          const age = item.age || 'N/A';
          const height = item.height || 'N/A';
          const weight = item.weight || 'N/A';
          const bmi = item.BMI || 'N/A';
          const pulse = item.pulse || 'N/A';
          const bloodPf = item.bloodPf || 'N/A';
          const bloodGroup = item.bloodGroup || 'N/A';
          const respiratoryRate = item.respiratoryRate || 'N/A';
          const temperature = item.temperature || 'N/A';
          const healthStatus = item.healthStatus || 'N/A';
          const vaccination = item.vaccination || '';
          const allergy = item.allergy || '';
          const medicalHistory = item.medicalHistory || '';
          const examination = item.examination || '';
          const assessmentDate = item.assessmentDate || 'N/A';
          const assessmentTime = this.formatTime(item.assessmentTime || 'N/A');
          
          return `
        <tr ${index % 2 === 0 ? 'class="even-row"' : ''}>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${startIndex + index + 1}</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${assessmentDate}</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${assessmentTime}</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">
            <div style="font-weight: bold; color: #2c3e50;">${studentName}</div>
          </td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${age}</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${height}</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${weight}</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${bmi}</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${pulse}</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${bloodPf}</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${bloodGroup}</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${respiratoryRate}</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${temperature}</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${healthStatus}</td>
        </tr>
      `;
        }).join('');
        
        // Add page break for multiple pages
        const pageBreak = page < totalPages - 1 ? 'page-break-after: always;' : '';
        
        allTableRows += `
          <div style="${pageBreak}">
            ${page > 0 ? `<div class="page-header"><h2>Health Assessment Report - Page ${page + 1} of ${totalPages}</h2></div>` : ''}
            <table class="report-table" style="border: 1px solid #ccc !important; border-collapse: collapse !important; border-spacing: 0 !important;">
              ${page === 0 ? `
        <thead>
          <tr>
            <th class="text-center" style="width: 3%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">S.NO</th>
            <th class="text-center" style="width: 8%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Date</th>
            <th class="text-center" style="width: 6%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Time</th>
            <th class="text-left" style="width: 18%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Student</th>
            <th class="text-center" style="width: 4%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Age</th>
            <th class="text-center" style="width: 5%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Height</th>
            <th class="text-center" style="width: 5%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Weight</th>
            <th class="text-center" style="width: 4%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">BMI</th>
            <th class="text-center" style="width: 5%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Pulse</th>
            <th class="text-center" style="width: 7%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Blood Pressure</th>
            <th class="text-center" style="width: 5%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Blood Group</th>
            <th class="text-center" style="width: 7%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Respiratory Rate</th>
            <th class="text-center" style="width: 5%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Temperature</th>
            <th class="text-center" style="width: 8%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Health Status</th>
          </tr>
        </thead>
              ` : ''}
              <tbody>
                ${tableRows}
              </tbody>
            </table>
          </div>
        `;
      }
      
      return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Health Assessment Report</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Arial', sans-serif;
              line-height: 1.4;
              color: #333;
              background-color: #fff !important;
              padding: 15px;
              font-size: 12px;
              margin: 0;
              width: 100%;
            }
            
            .print-header {
              text-align: center;
              margin-bottom: 10px;
              padding: 8px;
              border-radius: 6px;
              color: #212529;
              background: #f8f9fa;
            }
            
            .print-header h1 {
              margin: 0 0 5px 0;
              color: #212529;
              font-size: 20px;
            }
            
            .print-header p {
              font-size: 14px;
              opacity: 0.9;
              margin: 3px 0;
            }
            
            .page-header {
              text-align: center;
              margin-bottom: 20px;
              padding: 10px;
              background: #f8f9fa;
              border-radius: 6px;
              border: 1px solid #e9ecef;
            }
            
            .page-header h2 {
              margin: 0;
              color: #667eea;
              font-size: 18px;
              font-weight: bold;
            }
            
            .report-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 10px;
              background: white;
              border: 1px solid #ccc !important;
              border-spacing: 0 !important;
            }
            
            .report-table th {
              background-color: #e9ecef !important;
              color: #212529 !important;
              padding: 6px 8px;
              text-align: center;
              font-weight: bold;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              border: 1px solid #ccc !important;
            }
            
            .report-table td {
              padding: 6px 8px;
              border: 1px solid #ccc !important;
              font-size: 12px;
              vertical-align: middle;
            }
            
            .report-table tr.even-row {
              background-color: #f8f9fa;
            }
            
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .text-left { text-align: left; }
            
            .print-controls {
              position: fixed;
              top: 20px;
              right: 20px;
              z-index: 1000;
            }
            
            .print-btn {
              background: #6c757d;
              color: white;
              border: none;
              padding: 10px 16px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 13px;
              font-weight: bold;
              box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
              transition: all 0.3s ease;
            }
            
            .print-btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(108, 117, 125, 0.4);
            }
            
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              
              @page {
                margin: 0.5in !important;
                size: A4 !important;
              }
              
              html, body {
                margin: 0 !important;
                padding: 0 !important;
                background: white !important;
                width: 100% !important;
                height: 100% !important;
                font-size: 11px !important;
              }
              
              .print-controls {
                display: none !important;
              }
              
              .print-header {
                background: #f8f9fa !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .report-table th {
                background-color: #e9ecef !important;
                color: #212529 !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              @page {
                size: A4 landscape;
                margin: 0.5in;
                background: white !important;
              }
            }
          </style>
        </head>
        <body>
          <div style="background: white !important; width: 100%; min-height: 100vh; margin: 0; padding: 0;">
            <!-- Print Controls -->
            <div class="print-controls">
              <button id="printButton" class="print-btn">
                🖨️ Print Report
              </button>
            </div>
            
            <!-- Header -->
            <div class="print-header">
              <h1>Health Assessment Report</h1>
              <p>Generated on ${new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</p>
          </div>
          
          <!-- Main Table -->
          ${allTableRows}
          
          </div>
        </body>
        </html>
      `;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

// Simple Health Assessment View Modal Styles
// Clean styling without complex backgrounds and borders

// Validation styling for required fields
.is-invalid {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.vs__dropdown-toggle.is-invalid {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

// Required field indicators
.form-group label[required]::after {
  content: " *";
  color: #dc3545;
  font-weight: bold;
}

// Enhanced form styling
.form-control:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.vs__dropdown-toggle:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

// Filter section alignment
.filter-input {
  height: 38px;
}

.filter-btn {
  height: 38px;
  padding: 0.25rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.unit-input-group {
  width: 100%;
}

.unit-input-group .form-control {
  height: 42px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  display: flex;
  align-items: center;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.unit-input-group .input-group-text {
  height: 42px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  background: #f5f5f7;
  font-weight: 600;
  color: #6c757d;
  display: flex;
  align-items: center;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

// Health Assessments List Card
.health-assessments-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: visible;
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  z-index: 1;

  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem;
    right: 0.75rem;
    width: calc(100% - 1.5rem);
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
    width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }
  
  .filter-row-single-line {
    background: transparent !important;
    padding: 0;
    margin-bottom: 1rem;
  }
}

.table-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }

  small {
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: 500;
  }
}

// Table Scroll Container
.table-scroll-container {
  position: relative;
  width: 100%;
}

.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
  
  &::-webkit-scrollbar {
    height: 12px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  .table-scrollbar-content {
    height: 1px;
    width: 100%;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
}

.table-scrollbar-bottom {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-top: 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
  
  &::-webkit-scrollbar {
    height: 12px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  .table-scrollbar-content {
    height: 1px;
    width: 100%;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
}

.table-wrapper {
  position: relative;
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; // hide native horizontal bar (we show custom bars instead)
  
  &::-webkit-scrollbar {
    height: 0; // hide native horizontal bar
    background: transparent;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: transparent;
  }
  
  scrollbar-color: transparent transparent;
}

// Health Assessments Table - Modern Compact Design
.health-assessments-table {
  margin-top: 1rem;
  border-radius: 12px;
  overflow: hidden;
  min-width: 100%;
  width: max-content;
  border: 1px solid #e5e7eb;

  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid #e2e8f0;
  }
  
  ::v-deep td {
    padding: 0.875rem 0.875rem;
    border-bottom: 1px solid #f1f5f9;
    font-size: 0.8125rem;
    color: #475569;
    vertical-align: middle;
  }
  
  ::v-deep tbody tr {
    transition: all 0.2s ease;
    border-left: 3px solid transparent;
    
    &:hover {
      background: linear-gradient(90deg, #f8fafc 0%, #ffffff 100%);
      border-left-color: #7c5dfa;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.08);
      transform: translateX(2px);
    }
  }
  
  th, td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 8px 6px;
    font-size: 13px;
  }
  
  // Specific column styling
  th:nth-child(1), td:nth-child(1) { // S.No
    width: 50px;
    text-align: center;
  }
  
  th:nth-child(2), td:nth-child(2) { // Date
    width: 130px;
    text-align: center;
    font-weight: bold;
  }
  
  th:nth-child(3), td:nth-child(3) { // Time
    width: 80px;
    text-align: center;
    font-weight: bold;
  }
  
          th:nth-child(4), td:nth-child(4) { // Student Name
            width: 200px;
            text-align: left;
            font-weight: bold;
          }
  
  th:nth-child(5), td:nth-child(5) { // Age
    width: 60px;
    text-align: center;
  }
  
  th:nth-child(6), td:nth-child(6) { // Height
    width: 80px;
    text-align: center;
  }
  
  th:nth-child(7), td:nth-child(7) { // Weight
    width: 80px;
    text-align: center;
  }
  
  th:nth-child(8), td:nth-child(8) { // BMI
    width: 60px;
    text-align: center;
  }
  
  th:nth-child(9), td:nth-child(9) { // Pulse
    width: 70px;
    text-align: center;
  }
  
  th:nth-child(10), td:nth-child(10) { // Blood Pressure
    width: 120px;
    text-align: center;
  }
  
  th:nth-child(11), td:nth-child(11) { // Blood Group
    width: 100px;
    text-align: center;
  }
  
  th:nth-child(12), td:nth-child(12) { // Respiratory Rate
    width: 120px;
    text-align: center;
  }
  
  th:nth-child(13), td:nth-child(13) { // Temperature
    width: 100px;
    text-align: center;
  }
  
  th:nth-child(14), td:nth-child(14) { // Health Status
    width: 120px;
    text-align: center;
  }
  
  th:nth-child(15), td:nth-child(15) { // Actions
    width: 120px;
    text-align: center;
  }
}

// New search functionality styles
.search-section {
  margin-bottom: 16px;
}

.search-input {
  border-radius: 6px;
  border: 1px solid #ced4da;
  padding: 8px 12px;
  font-size: 14px;
  width: 100%;
}

.search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.search-results {
  position: relative;
  z-index: 1000;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  margin-top: 4px;
  /* Remove double scrollbar issue */
  overflow-x: hidden;
}

.student-options {
  /* Remove nested scrolling to prevent double scrollbars */
  max-height: none;
  overflow: visible;
}

.student-option {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  /* Ensure no internal scrolling */
  overflow: visible;
}

.student-option:hover {
  background-color: #f8f9fa;
  border-left: 3px solid #007bff;
  transform: translateX(2px);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
}

.student-option:last-child {
  border-bottom: none;
}

.student-option:active {
  transform: translateX(1px);
  background-color: #e3f2fd;
}

.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #6c757d;
}

.empty-state p {
  margin-top: 12px;
  font-size: 14px;
}

.loading-state {
  padding: 20px;
  text-align: center;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.selected-student {
  margin-top: 12px;
  padding: 12px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

// GR Number styling
.ml-2 {
  margin-left: 8px;
}

.mr-1 {
  margin-right: 4px;
}

.text-muted small .ml-2 {
  color: #6c757d;
  font-size: 12px;
  font-weight: 500;
}

// Custom scrollbar for search results - smooth and modern
.search-results::-webkit-scrollbar {
  width: 6px;
}

.search-results::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.search-results::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
  transition: background 0.2s ease;
}

.search-results::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

// Ensure no double scrollbars
.search-results * {
  overflow: visible !important;
}

.student-options * {
  overflow: visible !important;
}

// View Modal Compact Styles
.view-modal-content {
  font-size: 0.875rem;
  
  h6 {
    font-size: 0.95rem;
    margin-bottom: 0.5rem;
  }
  
  .measurement-card {
    border: 1px solid #e9ecef;
    transition: all 0.2s ease;
    min-height: 65px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    background-color: #ffffff;
    
    &:hover {
      background-color: #f8f9fa !important;
      border-color: #dee2e6;
    }
  }
  
  .medical-info-card {
    border: 1px solid #e9ecef;
    transition: all 0.2s ease;
    min-height: 60px;
    background-color: #ffffff;
    
    &:hover {
      background-color: #f8f9fa !important;
      border-color: #dee2e6;
    }
  }
  
  .bg-light {
    background-color: #f8f9fa !important;
  }
}

// Filter Controls
.filter-control {
  height: 38px;
  border-radius: 8px;
  border: 1px solid #e0d8ff;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
  }
}

.clear-btn-circle {
  width: 38px;
  height: 38px;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0d8ff;
  background: #ffffff;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f7ff;
    border-color: #7c5dfa;
    transform: scale(1.05);
  }
}

.action-btn-filter {
  height: 38px;
  border-radius: 10px;
  padding: 0 1rem;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
  }
}

.print-btn-icon {
  width: 38px;
  height: 38px;
  padding: 0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0d8ff;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f7ff;
    border-color: #7c5dfa;
    transform: scale(1.05);
  }
}

// Pagination Controls
.pagination-controls-top {
  padding: 0.75rem 0;
  border-top: 1px solid #f0edff;
  border-bottom: 1px solid #f0edff;
  margin: 1rem 0;
  flex-wrap: wrap;
  gap: 1rem;
}

.pagination-switch-compact {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ::v-deep .custom-control-label {
    font-size: 0.8125rem;
    color: #6b7280;
    cursor: pointer;
  }
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-size-label {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
  white-space: nowrap;
}

.page-size-select-compact {
  min-width: 80px;
  
  ::v-deep .vs__dropdown-toggle {
    height: 32px;
    padding: 0 0.5rem;
    border-radius: 6px;
    border: 1px solid #e0d8ff;
    font-size: 0.8125rem;
  }
}

.pagination-count-display {
  flex: 1;
  text-align: center;
  
  .count-text {
    font-size: 0.8125rem;
    color: #6b7280;
    
    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }
}

.pagination-wrapper-compact {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-compact {
  margin: 0;
  
  ::v-deep .page-item {
    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      border-radius: 6px;
      border: 1px solid #e0d8ff;
      color: #6b7280;
      margin: 0 0.125rem;
      transition: all 0.2s ease;
      
      &:hover {
        background: #f8f7ff;
        border-color: #7c5dfa;
        color: #7c5dfa;
      }
    }
    
    &.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #ffffff;
    }
  }
}

.gap-2 {
  gap: 0.5rem;
}

.action-buttons-group {
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* Add/Edit modal polish */
.assessment-modal ::v-deep .modal-header {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border-bottom: 2px solid #e0d8ff;
  padding: 1.25rem 1.75rem;
  border-radius: 16px 16px 0 0;
}

.assessment-modal ::v-deep .modal-title {
  font-weight: 800;
  color: #2d1b69;
}

.assessment-modal ::v-deep .modal-body {
  background: #ffffff;
  border-radius: 0 0 16px 16px;
  padding: 1.25rem 1.5rem;
}

.form-card {
  margin-bottom: 0.75rem;
}

.soft-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e8e5fb;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.08);
}

.input-compact {
  height: 40px !important;
  border-radius: 10px !important;
  border: 1.5px solid #e0d8ff !important;
  padding: 0.45rem 0.75rem !important;
  font-size: 0.95rem !important;

  &:focus {
    border-color: #7c5dfa !important;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.12) !important;
  }
}

.compact-input-group .input-group-text {
  border-radius: 0 10px 10px 0 !important;
  border: 1.5px solid #e0d8ff !important;
  border-left: 0 !important;
  font-weight: 700;
  color: #6b7280;
}

.compact-input-group .form-control {
  border-radius: 10px 0 0 10px !important;
  border-right: 0 !important;
}

// Skeleton Loader Styles - Premium Animated Effect
.skeleton-row {
  animation: skeletonPulse 2s ease-in-out infinite;
  transition: opacity 0.3s ease;
  
  &:nth-child(1) {
    animation-delay: 0s;
  }
  
  &:nth-child(2) {
    animation-delay: 0.15s;
  }
  
  &:nth-child(3) {
    animation-delay: 0.3s;
  }
  
  &:nth-child(4) {
    animation-delay: 0.45s;
  }
  
  &:nth-child(5) {
    animation-delay: 0.6s;
  }
  
  &:hover {
    background: transparent !important;
    border-left-color: transparent !important;
    box-shadow: none !important;
    transform: none !important;
    
    td {
      background: transparent !important;
      color: inherit !important;
      font-weight: normal !important;
    }
  }
}

.skeleton-cell {
  padding: 0.875rem 0.875rem !important;
  vertical-align: middle !important;
  border-top: 1px solid #f1f5f9 !important;
  border-bottom: 1px solid #f8fafc !important;
  background: #ffffff !important;
  position: relative;
  
  &:nth-child(even) {
    background: #fafbfc !important;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 4px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.03) 50%,
      transparent 100%
    );
    animation: skeletonBorderGlow 2.5s ease-in-out infinite;
    pointer-events: none;
  }
}

.skeleton-loader {
  height: 18px;
  border-radius: 10px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #f8f8f8 25%,
    #f0f0f0 50%,
    #f8f8f8 75%,
    #f0f0f0 100%
  );
  background-size: 300% 100%;
  animation: skeletonShimmer 2s ease-in-out infinite;
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.6) 50%,
      transparent 100%
    );
    animation: skeletonShine 2s ease-in-out infinite;
    filter: blur(8px);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.08) 30%,
      rgba(157, 122, 255, 0.12) 50%,
      rgba(124, 93, 250, 0.08) 70%,
      transparent 100%
    );
    animation: skeletonColorWave 2.5s ease-in-out infinite;
    border-radius: 10px;
  }
}

// Different widths for different column positions
.skeleton-cell {
  &:nth-child(1) .skeleton-loader {
    width: 50px;
    margin: 0 auto;
  }
  
  &:nth-child(2) .skeleton-loader {
    width: 70px;
  }
  
  &:nth-child(3) .skeleton-loader {
    width: 140px;
  }
  
  &:nth-child(4) .skeleton-loader {
    width: 110px;
  }
  
  &:nth-child(5) .skeleton-loader {
    width: 100px;
  }
  
  &:nth-child(6) .skeleton-loader {
    width: 100px;
  }
  
  &:nth-child(7) .skeleton-loader {
    width: 60px;
  }
  
  &:nth-child(8) .skeleton-loader {
    width: 90px;
  }
  
  &:nth-child(9) .skeleton-loader {
    width: 180px;
  }
  
  &:nth-child(10) .skeleton-loader {
    width: 180px;
  }
  
  &:nth-child(11) .skeleton-loader {
    width: 120px;
  }
  
  &:nth-child(12) .skeleton-loader {
    width: 100px;
  }
  
  &:nth-child(13) .skeleton-loader {
    width: 100px;
  }
  
  &:nth-child(14) .skeleton-loader {
    width: 120px;
  }
  
  &:nth-child(15) .skeleton-loader {
    width: 100px;
  }
  
  &:nth-child(16) .skeleton-loader {
    width: 120px;
  }
}

@keyframes skeletonShimmer {
  0% {
    background-position: -300% 0;
  }
  100% {
    background-position: 300% 0;
  }
}

@keyframes skeletonShine {
  0% {
    left: -100%;
  }
  50% {
    left: 100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes skeletonColorWave {
  0%, 100% {
    opacity: 0.3;
    transform: scaleX(0.8);
  }
  50% {
    opacity: 0.6;
    transform: scaleX(1);
  }
}

@keyframes skeletonBorderGlow {
  0%, 100% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

@keyframes skeletonPulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(0.998);
  }
}

@keyframes slideUpFade {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

// Actions cell styling (aligned with comment bank)
.actions-cell {
  min-width: 120px;
  max-width: 160px;
  vertical-align: middle !important;
}

.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

.action-btn-outline {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: #ffffff !important;
  border: 2px solid #ef4444 !important;
  color: #ef4444 !important;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.15);
  
  &:hover:not(:disabled) {
    background: #fef2f2 !important;
    border-color: #dc2626 !important;
    color: #dc2626 !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

// View modal polish
::v-deep .view-details-modal .modal-header {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border-bottom: 2px solid #e0d8ff;
  padding: 1.25rem 1.75rem;
  border-radius: 16px 16px 0 0;
}

::v-deep .view-details-modal .modal-title {
  font-weight: 800;
  color: #2d1b69;
}

::v-deep .view-details-modal .modal-body {
  background: #ffffff;
  border-radius: 0 0 16px 16px;
}

// View modal content styling
.view-info-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 14px;
  padding: 1rem 1.25rem;
  box-shadow: 0 6px 18px rgba(115, 103, 240, 0.08);
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.info-left .info-name {
  display: flex;
  align-items: center;
  gap: 0.6rem;

  h5 {
    font-weight: 800;
    color: #2d1b69;
    margin: 0;
    letter-spacing: -0.2px;
  }
}

.info-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.status-chip {
  font-weight: 700;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  text-transform: capitalize;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.view-section {
  margin-bottom: 1.25rem;
}

.section-header h6 {
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.2px;
}

.metrics-grid {
  display: grid;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.metrics-grid.four-cols {
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
}

.metrics-grid.two-cols {
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
}

.metric-card {
  background: #ffffff;
  border: 1px solid #e8e5fb;
  border-radius: 12px;
  padding: 0.9rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.08);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(115, 103, 240, 0.14);
    border-color: #d8d2ff;
  }
}

.metric-label {
  display: block;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #8a86b7;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.metric-value {
  display: block;
  font-size: 1.1rem;
  font-weight: 800;
  color: #2d1b69;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.info-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e8e5fb;
  border-radius: 12px;
  padding: 0.9rem 1rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.08);
  min-height: 120px;
}

.info-text {
  margin: 0.35rem 0 0;
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.5;
}
</style>

